import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { KeycloakService } from 'keycloak-angular';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { DataChangedService } from './data-changed.service';
import { ClientDialogComponent } from './dialogs/client-dialog/client-dialog.component';
import {
  ProjectDialogComponent,
  ProjectDialogData,
} from './dialogs/project-dialog/project-dialog.component';
import { SettingsDialogComponent } from './dialogs/settings-dialog/settings-dialog.component';
import { UserService } from './user.service';
import { TranslateService } from '@ngx-translate/core';
import {
  LanguageService,
  SupportedLanguage,
} from './services/language.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {
  private readonly SIDENAV_STATE_STORAGE_KEY = 'sidenav-expanded';

  public userData = {
    email: 'Nicht angemeldet',
    name: '',
  };

  SIDENAV_EXPANDEND_WIDTH = 350;
  SIDENAV_COLLAPSED_WIDTH = 250;

  public headline = this.dataChangedService.healdine;
  public sidenavWidth = this.SIDENAV_COLLAPSED_WIDTH;

  public availableLanguages: {
    code: SupportedLanguage;
    label: string;
    abbrev: string;
  }[] = [
    { code: 'de-AT', label: 'Deutsch', abbrev: 'DE' },
    { code: 'it', label: 'Italiano', abbrev: 'IT' },
  ];

  constructor(
    private keycloakService: KeycloakService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private dataChangedService: DataChangedService,
    public userService: UserService,
    private translateService: TranslateService,
    private languageService: LanguageService
  ) {}

  public ngOnInit() {
    this.userData.name = this.userService.fullname;
    this.userData.email = this.userService.email;

    this.sidenavWidth = this.loadSidenavWidth();
  }

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe(Breakpoints.Tablet)
    .pipe(
      map((result) => result.matches),
      shareReplay()
    );

  /**
   * logout
   */
  public logout() {
    this.keycloakService.logout();
  }

  public addProject() {
    this.dialog.open(ProjectDialogComponent, {
      data: <ProjectDialogData>{},
    });
  }

  public addClient() {
    this.dialog.open(ClientDialogComponent, {
      data: {},
    });
  }

  public userLogout() {
    this.keycloakService.logout();
  }

  public setHeadlineImageVisibility(event: Event, isVisible: boolean): void {
    (event.target as HTMLImageElement).hidden = !isVisible;
  }

  public sidenavWidthChange() {
    if (this.sideNavIsExpanded) {
      this.sidenavWidth = this.SIDENAV_COLLAPSED_WIDTH;
    } else {
      this.sidenavWidth = this.SIDENAV_EXPANDEND_WIDTH;
    }
    this.saveSidenavState();
  }

  get sideNavIsExpanded(): boolean {
    return this.sidenavWidth === this.SIDENAV_EXPANDEND_WIDTH;
  }

  private loadSidenavWidth(): number {
    try {
      return localStorage.getItem(this.SIDENAV_STATE_STORAGE_KEY) === 'true'
        ? this.SIDENAV_EXPANDEND_WIDTH
        : this.SIDENAV_COLLAPSED_WIDTH;
    } catch {
      return this.SIDENAV_COLLAPSED_WIDTH;
    }
  }

  private saveSidenavState(): void {
    try {
      localStorage.setItem(
        this.SIDENAV_STATE_STORAGE_KEY,
        String(this.sideNavIsExpanded)
      );
    } catch {
      // Keep the current in-memory state when browser storage is unavailable.
    }
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): SupportedLanguage {
    return this.languageService.getCurrentLanguage();
  }

  /**
   * Get current language abbreviation
   */
  getCurrentLanguageAbbrev(): string {
    const lang = this.getCurrentLanguage();
    const found = this.availableLanguages.find((l) => l.code === lang);
    return found ? found.abbrev : 'DE';
  }

  /**
   * Switch language
   */
  switchLanguage(language: SupportedLanguage): void {
    this.languageService.setLanguage(language);
    // Reload the page to apply locale changes (LOCALE_ID and MAT_DATE_LOCALE)
    window.location.reload();
  }
}
